import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import {
  authApi,
  invalidatePermissionsReadCaches,
} from '../services/api';
import { useAuth } from './AuthContext';

export type PermissionsContextValue = {
  permissions: string[];
  loading: boolean;
  hasPermission: (code: string) => boolean;
  hasSuperAdminRole: boolean;
  securityCommandControlLogEnabled: boolean;
  mandatoryScheduleInputEnabled: boolean;
  healthcareComplianceEnabled: boolean;
  healthcareNurseBandingPayrollEnabled: boolean;
  refresh: () => Promise<void>;
  hasDashboard: boolean;
  defaultHomePath: string | null;
  hasTaskManagement: boolean;
  hasMessageManagement: boolean;
  hasHolidayManagement: boolean;
  hasIncidentManagement: boolean;
  hasEmployeeManagement: boolean;
  hasScheduling: boolean;
  hasAttendanceAdmin: boolean;
  hasTimecards: boolean;
  hasReports: boolean;
  hasSettings: boolean;
  hasJobMonitor: boolean;
  hasLeavePolicies: boolean;
  hasJobApplicantTracking: boolean;
  hasDocumentsPolicies: boolean;
  hasManagerInbox: boolean;
  hasTimeStationClockInOut: boolean;
}

const PermissionsContext = createContext<PermissionsContextValue | undefined>(undefined);

const PERMISSIONS_SESSION_CACHE_KEY = 'staffly:permissionsCache';
const PERMISSIONS_LOCAL_CACHE_KEY = 'staffly:permissionsLocalCache';

function readPermissionsCache(employeeId: number): {
  permissions: string[];
  hasSuperAdminRole: boolean;
  managesTeam: boolean;
  securityCommandControlLogEnabled: boolean;
  mandatoryScheduleInputEnabled: boolean;
  healthcareComplianceEnabled: boolean;
  healthcareNurseBandingPayrollEnabled: boolean;
} | null {
  const readFrom = (raw: string | null) => {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.employeeId !== employeeId || !Array.isArray(parsed.permissions)) return null;
      return {
        permissions: parsed.permissions,
        hasSuperAdminRole: Boolean(parsed.hasSuperAdminRole),
        managesTeam: Boolean(parsed.managesTeam),
        securityCommandControlLogEnabled: Boolean(parsed.securityCommandControlLogEnabled),
        mandatoryScheduleInputEnabled: Boolean(parsed.mandatoryScheduleInputEnabled),
        healthcareComplianceEnabled: Boolean(parsed.healthcareComplianceEnabled),
        healthcareNurseBandingPayrollEnabled: Boolean(parsed.healthcareNurseBandingPayrollEnabled),
      };
    } catch {
      return null;
    }
  };

  return readFrom(sessionStorage.getItem(PERMISSIONS_SESSION_CACHE_KEY))
    ?? readFrom(localStorage.getItem(PERMISSIONS_LOCAL_CACHE_KEY));
}

function readPermissionsSessionCache(employeeId: number): {
  permissions: string[];
  hasSuperAdminRole: boolean;
  managesTeam: boolean;
  securityCommandControlLogEnabled: boolean;
  mandatoryScheduleInputEnabled: boolean;
  healthcareComplianceEnabled: boolean;
  healthcareNurseBandingPayrollEnabled: boolean;
} | null {
  return readPermissionsCache(employeeId);
}

function writePermissionsSessionCache(
  employeeId: number,
  data: {
    permissions: string[];
    hasSuperAdminRole: boolean;
    managesTeam: boolean;
    securityCommandControlLogEnabled: boolean;
    mandatoryScheduleInputEnabled: boolean;
    healthcareComplianceEnabled: boolean;
    healthcareNurseBandingPayrollEnabled: boolean;
  }
): void {
  try {
    const payload = JSON.stringify({ employeeId, ...data });
    sessionStorage.setItem(PERMISSIONS_SESSION_CACHE_KEY, payload);
    localStorage.setItem(PERMISSIONS_LOCAL_CACHE_KEY, payload);
  } catch {
    // ignore quota errors
  }
}

function readInitialPermissionsCache(): ReturnType<typeof readPermissionsCache> {
  try {
    const saved = localStorage.getItem('employee');
    const id = saved ? JSON.parse(saved)?.id : null;
    if (typeof id === 'number') return readPermissionsCache(id);
  } catch {
    /* ignore */
  }
  return null;
}

export const PermissionsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { employee, loading: authLoading, updateEmployee } = useAuth();
  const initialCache = readInitialPermissionsCache();

  const [permissions, setPermissions] = useState<string[]>(() => initialCache?.permissions ?? []);
  const [hasSuperAdminRole, setHasSuperAdminRole] = useState(() => Boolean(initialCache?.hasSuperAdminRole));
  const [managesTeam, setManagesTeam] = useState(() => Boolean(initialCache?.managesTeam));
  const [securityCommandControlLogEnabled, setSecurityCommandControlLogEnabled] = useState(
    () => Boolean(initialCache?.securityCommandControlLogEnabled)
  );
  const [mandatoryScheduleInputEnabled, setMandatoryScheduleInputEnabled] = useState(
    () => Boolean(initialCache?.mandatoryScheduleInputEnabled)
  );
  const [healthcareComplianceEnabled, setHealthcareComplianceEnabled] = useState(
    () => Boolean(initialCache?.healthcareComplianceEnabled)
  );
  const [healthcareNurseBandingPayrollEnabled, setHealthcareNurseBandingPayrollEnabled] = useState(
    () => Boolean(initialCache?.healthcareNurseBandingPayrollEnabled)
  );
  const [loading, setLoading] = useState(() => !initialCache);

  const load = useCallback(async (bustCache = false) => {
    /* Wait for auth restore before treating empty permissions as "no dashboard" (avoids flash redirect). */
    if (authLoading || !employee?.id) {
      if (!authLoading && !employee?.id) {
        setPermissions([]);
        setHasSuperAdminRole(false);
        setManagesTeam(false);
        setSecurityCommandControlLogEnabled(false);
        setMandatoryScheduleInputEnabled(false);
        setHealthcareComplianceEnabled(false);
        setHealthcareNurseBandingPayrollEnabled(false);
        setLoading(false);
      } else {
        setLoading(true);
      }
      return;
    }
    if (bustCache) {
      invalidatePermissionsReadCaches(employee.id);
    }
    const cached = !bustCache ? readPermissionsSessionCache(employee.id) : null;
    if (cached) {
      setPermissions(cached.permissions);
      setHasSuperAdminRole(cached.hasSuperAdminRole);
      setManagesTeam(cached.managesTeam);
      setSecurityCommandControlLogEnabled(cached.securityCommandControlLogEnabled);
      setMandatoryScheduleInputEnabled(cached.mandatoryScheduleInputEnabled);
      setHealthcareComplianceEnabled(cached.healthcareComplianceEnabled);
      setHealthcareNurseBandingPayrollEnabled(cached.healthcareNurseBandingPayrollEnabled);
      setLoading(false);
    } else if (!bustCache) {
      // First load only — a post-save refresh must not unmount the current page.
      setLoading(true);
    }
    try {
      const session = await authApi.getSession();
      setPermissions(session.permissionCodes ?? []);
      setHasSuperAdminRole(Boolean(session.hasSuperAdminRole));
      setManagesTeam(Boolean(session.managesTeam));
      setSecurityCommandControlLogEnabled(Boolean(session.securityCommandControlLogEnabled));
      setMandatoryScheduleInputEnabled(Boolean(session.mandatoryScheduleInputEnabled));
      setHealthcareComplianceEnabled(Boolean(session.healthcareComplianceEnabled));
      setHealthcareNurseBandingPayrollEnabled(Boolean(session.healthcareNurseBandingPayrollEnabled));
      writePermissionsSessionCache(employee.id, {
        permissions: session.permissionCodes ?? [],
        hasSuperAdminRole: Boolean(session.hasSuperAdminRole),
        managesTeam: Boolean(session.managesTeam),
        securityCommandControlLogEnabled: Boolean(session.securityCommandControlLogEnabled),
        mandatoryScheduleInputEnabled: Boolean(session.mandatoryScheduleInputEnabled),
        healthcareComplianceEnabled: Boolean(session.healthcareComplianceEnabled),
        healthcareNurseBandingPayrollEnabled: Boolean(session.healthcareNurseBandingPayrollEnabled),
      });
      if (
        session.assignedLocations &&
        session.assignedLocations.length > 0 &&
        !employee.assignedLocations?.length
      ) {
        updateEmployee({
          ...employee,
          assignedLocations: session.assignedLocations,
          profile: {
            ...employee.profile,
            mainLocationId: session.mainLocationId ?? employee.profile?.mainLocationId,
          },
        });
      }
    } catch {
      setPermissions([]);
      setHasSuperAdminRole(false);
      setSecurityCommandControlLogEnabled(false);
      setMandatoryScheduleInputEnabled(false);
      setHealthcareComplianceEnabled(false);
      setHealthcareNurseBandingPayrollEnabled(false);
    } finally {
      setLoading(false);
    }
  }, [employee?.id, authLoading]);

  useEffect(() => {
    void load(false);
  }, [load]);

  useEffect(() => {
    const onRefresh = () => {
      void load(true);
    };
    window.addEventListener('staffly:refresh-permissions', onRefresh);
    return () => window.removeEventListener('staffly:refresh-permissions', onRefresh);
  }, [load]);

  const hasPermission = useCallback(
    (code: string) => permissions.some((c) => c.toLowerCase() === code.toLowerCase()),
    [permissions]
  );

  const flags = useMemo(() => {
    const p = (code: string) => permissions.some((c) => c.toLowerCase() === code.toLowerCase());
    const hasDashboard = p('dashboard');

    let defaultHomePath: string | null = null;
    if (hasDashboard) {
      defaultHomePath = '/';
    } else if (managesTeam) {
      defaultHomePath = '/inbox';
    } else if (p('my_attendances')) {
      defaultHomePath = '/my-attendances';
    } else if (p('my_schedules')) {
      defaultHomePath = '/my-schedules';
    } else if (p('my_tasks')) {
      defaultHomePath = '/my-tasks';
    } else if (p('my_messages')) {
      defaultHomePath = '/my-messages';
    } else if (p('my_trainings')) {
      defaultHomePath = '/my-trainings';
    } else if (p('my_licenses')) {
      defaultHomePath = '/my-licenses';
    } else if (p('holidays')) {
      defaultHomePath = '/my-holidays';
    } else if (p('incidents')) {
      defaultHomePath = '/my-incidents';
    }

    const hasTaskManagement = p('task_management') || p('task_admin');
    const hasMessageManagement = p('message_management') || p('message_admin');
    const hasHolidayManagement = p('holiday_management') || p('holiday_admin');
    const hasIncidentManagement = p('incident_management') || p('incident_admin');
    const hasEmployeeManagement = p('employee_management') || p('employee_admin');
    const hasScheduling =
      p('scheduling') || p('schedule_management') || p('schedule_admin');
    const hasAttendanceAdmin =
      p('attendance') || p('attendance_management') || p('attendance_admin');
    const hasTimecards =
      p('timecard_management') ||
      p('timecard_admin') ||
      p('timecards') ||
      hasAttendanceAdmin;
    const hasReports =
      hasScheduling || hasTimecards || hasAttendanceAdmin || p('payroll_management');
    const hasSettings = p('settings');
    const hasJobMonitor = hasSettings;
    const hasJobApplicantTracking =
      p('job_applicant_tracking') || hasEmployeeManagement || hasSettings;
    const hasDocumentsPolicies = p('documents_policies');
    const hasTimeStationClockInOut = p('time_station_clock_in_out');

    return {
      hasDashboard,
      defaultHomePath,
      hasTaskManagement,
      hasMessageManagement,
      hasHolidayManagement,
      hasIncidentManagement,
      hasEmployeeManagement,
      hasScheduling,
      hasAttendanceAdmin,
      hasTimecards,
      hasReports,
      hasSettings,
      hasJobMonitor,
      hasLeavePolicies: hasSettings || hasHolidayManagement,
      hasJobApplicantTracking,
      hasDocumentsPolicies,
      hasManagerInbox: managesTeam,
      hasTimeStationClockInOut,
    };
  }, [permissions, managesTeam]);

  const value = useMemo<PermissionsContextValue>(
    () => ({
      permissions,
      loading,
      hasPermission,
      hasSuperAdminRole,
      securityCommandControlLogEnabled,
      mandatoryScheduleInputEnabled,
      healthcareComplianceEnabled,
      healthcareNurseBandingPayrollEnabled,
      refresh: () => load(true),
      ...flags,
    }),
    [
      permissions,
      loading,
      hasPermission,
      hasSuperAdminRole,
      managesTeam,
      securityCommandControlLogEnabled,
      mandatoryScheduleInputEnabled,
      healthcareComplianceEnabled,
      healthcareNurseBandingPayrollEnabled,
      load,
      flags,
    ]
  );

  return (
    <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>
  );
};

export function usePermissions(): PermissionsContextValue {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}
